import { memo } from "react";
import clsx from "clsx";
import { Icon } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useInboxBadge } from "../../app/hooks/useInbox.js";
import { isActiveTab, mobileNavIcon, mobileNavRoute } from "./mobileTabs.js";
import styles from "./MobileShell.module.scss";

type MobileTab = components["schemas"]["MobileTab"];

export interface MobileTabBarProps {
  /** The ordered 5-tab set from the server nav document (`nav.mobile.tabs`). */
  tabs: MobileTab[];
  /** The current pathname, for the active-tab highlight. */
  activeRoute: string;
  onNavigate?: (route: string) => void;
  /** Open the More bottom sheet (the `more` tab has no route). */
  onOpenMore: () => void;
  /** Keyboard-up hides the tab bar — slid off-screen + removed from the tab order.*/
  hidden: boolean;
}

/**
 * The role-keyed 5-tab bottom bar. Renders exactly the server-granted tabs — no client role→tab
 * matrix. The center tab (`tab.center`, index 2) renders as the lifted ink-square DOS button; for the office
 * roles that center is their primary surface (releases/shows) in the same treatment.
 *
 * `memo`'d: the shell re-renders on every modal/sheet toggle, but the bar only needs to repaint when a tab
 * moves (`activeRoute`) or the tab set changes — so its callback props are stabilized at the call site.
 */
export const MobileTabBar = memo(function MobileTabBar({
  tabs,
  activeRoute,
  onNavigate,
  onOpenMore,
  hidden,
}: MobileTabBarProps) {
  // The Inbox tab's badge counts THREADS with unread messages (useInboxBadge) — never the notification
  // count, which belongs to the bell (CD inbox-badge 2026-07-28: "only actual inbox messages should
  // alert the bottom inbox"; a friend request lighting the Inbox tab was the bug). Two badges, two sources.
  const inboxBadge = useInboxBadge();

  return (
    <nav
      className={clsx(styles.tabbar, hidden && styles.tabbarHidden)}
      aria-label="Primary"
      aria-hidden={hidden || undefined}
    >
      {tabs.map((tab) => {
        const key = tab.key ?? "";
        const Glyph = mobileNavIcon(key);
        const active = !tab.center && isActiveTab(key, activeRoute);
        const badge = key === "inbox" ? (inboxBadge.data ?? 0) : 0;
        const onClick =
          key === "more"
            ? onOpenMore
            : () => {
                const route = mobileNavRoute(key);
                if (route) onNavigate?.(route);
              };
        return (
          <button
            key={key}
            type="button"
            className={clsx(styles.tab, tab.center && styles.tabCenter, active && styles.tabActive)}
            aria-current={active ? "page" : undefined}
            // The count lives in the BUTTON's accessible name (the MobileHeader bell pattern — a child's
            // aria-label is not folded into a control that already names itself); the badge is decorative.
            aria-label={badge > 0 ? `${tab.label ?? key}, ${badge} unread` : (tab.label ?? key)}
            tabIndex={hidden ? -1 : undefined}
            onClick={onClick}
          >
            <span className={styles.tabIcon}>
              <Icon icon={Glyph} size={tab.center ? 22 : 20} />
              {badge > 0 && (
                <span className={styles.tabBadge} aria-hidden="true">
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </span>
            <span className={styles.tabLabel}>{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
});
